import { ReviewForm } from '@/components/book/review-form';
import { useTranslations } from '@/hooks/use-translations';
import type { BookReview } from '@/types/models';
import { Star } from 'lucide-react';
import { useState } from 'react';

interface BookReviewsSectionProps {
    reviews?: BookReview[];
    bookTitle: string;
    bookSlug: string;
}

export function BookReviewsSection({ reviews, bookTitle, bookSlug }: BookReviewsSectionProps) {
    const { t } = useTranslations();

    // Pagination state
    const [currentPage, setCurrentPage] = useState(1);
    const reviewsPerPage = 5;
    const totalPages = reviews ? Math.ceil(reviews.length / reviewsPerPage) : 0;
    const startIndex = (currentPage - 1) * reviewsPerPage;
    const endIndex = startIndex + reviewsPerPage;
    const currentReviews = reviews?.slice(startIndex, endIndex) || [];

    const hasReviews = reviews && reviews.length > 0;

    return (
        <section className="py-8 md:py-16">
            <div className="container mx-auto px-4">
                <div className="mx-auto max-w-4xl">
                    <div className="mb-8 flex items-center gap-3 md:mb-12 md:gap-4">
                        <Star className="h-4 w-4 text-muted-foreground md:h-5 md:w-5" />
                        <h2 className="text-xl font-medium md:text-2xl">{hasReviews ? t('Community Thoughts') : t('Be the First to Review')}</h2>
                        <div className="h-px flex-1 bg-border" />
                    </div>

                    {!hasReviews && (
                        <div className="rounded-lg border bg-muted/20 p-6 text-center md:p-8">
                            <p className="mb-6 text-muted-foreground">{t('No reviews yet. Be the first to share your thoughts!')}</p>
                        </div>
                    )}

                    {hasReviews && (
                        <div className="space-y-6 md:space-y-8">
                            {currentReviews.map((review) => (
                                <div key={review.id} className="border p-4 md:p-6">
                                    <div className="mb-4 flex items-center justify-between">
                                        <div>
                                            <div className="mb-2 flex items-center gap-3">
                                                <span className="font-medium">{review.reviewer_name}</span>
                                                <div className="flex items-center gap-1">
                                                    {Array.from({ length: 5 }).map((_, i) => (
                                                        <Star
                                                            key={i}
                                                            className={`h-3 w-3 ${
                                                                i < review.rating ? 'fill-yellow-400 text-yellow-400' : 'fill-gray-300 text-gray-300'
                                                            }`}
                                                        />
                                                    ))}
                                                </div>
                                            </div>
                                        </div>
                                        <time className="text-xs text-muted-foreground sm:text-sm">
                                            {new Date(review.review_date).toLocaleDateString('en-GB')}
                                        </time>
                                    </div>

                                    <p className="text-sm leading-relaxed">{review.review_text}</p>
                                </div>
                            ))}
                        </div>
                    )}

                    {hasReviews && totalPages > 1 && (
                        <div className="mt-8 flex items-center justify-center gap-1 sm:gap-2 md:mt-12">
                            <button
                                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
                                disabled={currentPage === 1}
                                className="border px-2 py-1.5 text-xs hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50 sm:px-4 sm:py-2 sm:text-sm"
                            >
                                {t('Previous')}
                            </button>

                            <div className="flex gap-0.5 sm:gap-1">
                                {Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => {
                                    // Show first page, last page, current page, and pages around current
                                    if (page === 1 || page === totalPages || (page >= currentPage - 1 && page <= currentPage + 1)) {
                                        return (
                                            <button
                                                key={page}
                                                onClick={() => setCurrentPage(page)}
                                                className={`border px-2 py-1.5 text-xs sm:px-3 sm:py-2 sm:text-sm ${
                                                    currentPage === page ? 'bg-foreground text-background' : 'hover:bg-muted'
                                                }`}
                                            >
                                                {page}
                                            </button>
                                        );
                                    } else if (page === currentPage - 2 || page === currentPage + 2) {
                                        return (
                                            <span key={page} className="self-center px-0.5 text-xs sm:px-1 sm:text-sm">
                                                ...
                                            </span>
                                        );
                                    }
                                    return null;
                                })}
                            </div>

                            <button
                                onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
                                disabled={currentPage === totalPages}
                                className="border px-2 py-1.5 text-xs hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50 sm:px-4 sm:py-2 sm:text-sm"
                            >
                                {t('Next')}
                            </button>
                        </div>
                    )}

                    {/* Review Submission Form */}
                    <div className={hasReviews ? 'mt-12 border-t pt-12' : 'mt-8'}>
                        <h3 className="mb-6 text-lg font-medium md:text-xl">{t('Leave Your Review')}</h3>
                        <ReviewForm bookSlug={bookSlug} bookTitle={bookTitle} />
                    </div>
                </div>
            </div>
        </section>
    );
}
